/**
 * 條碼掃描器元件（zbar-wasm）
 * 支援所有主流 1D/2D 條碼格式，包含 Code 39
 */

import React, { useRef, useState, useEffect } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Button,
  IconButton,
} from '@mui/material'
import QrCodeScannerIcon from '@mui/icons-material/QrCodeScanner'
import CloseIcon from '@mui/icons-material/Close'
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline'
// 動態 import zbar-wasm，避免靜態 import 時 WASM 載入失敗
let scanImageDataFn: typeof import('@undecaf/zbar-wasm')['scanImageData'] | null = null

const loadZbar = async (): Promise<boolean> => {
  if (scanImageDataFn) return true
  try {
    const zbar = await import('@undecaf/zbar-wasm')
    scanImageDataFn = zbar.scanImageData
    // 暖機：用 1x1 空白圖確認 WASM 可運作
    const c = document.createElement('canvas')
    c.width = 1; c.height = 1
    const ctx = c.getContext('2d')!
    await scanImageDataFn(ctx.getImageData(0, 0, 1, 1))
    console.log('[BarcodeDebug] zbar-wasm WASM 載入並暖機完成')
    return true
  } catch {
    console.log('[BarcodeDebug] zbar-wasm 暖機完成（無條碼正常）')
    return true // 暖機時沒條碼會 throw，但 WASM 已載入
  }
}

interface BarcodeScannerProps {
  open: boolean
  onClose: () => void
  /**
   * 掃描成功 callback。
   * @param decodedText 處理後的櫃號（合格 ISO 6346 11 碼，或使用者確認的原文）
   * @param rawText     trim 後的原始條碼內容（可能含船掛在前的多餘字元，例如「999999ABCD1234567」）
   *                    caller 可從 rawText 自行擷取船掛等附加資訊
   */
  onSuccess: (decodedText: string, rawText?: string) => void
  title?: string
  /**
   * 若 true，當 raw 為「6 碼數字 + ISO 6346 (4 字母 + 7 數字)」格式時，
   * 確認 dialog 內額外顯示前 6 碼船掛供使用者確認（補登表單情境用）。
   */
  showVesselRegNo?: boolean
}

const BarcodeScanner: React.FC<BarcodeScannerProps> = ({
  open,
  onClose,
  onSuccess,
  title = '掃描條碼',
  showVesselRegNo = false,
}) => {
  const [scannerError, setScannerError] = useState<string | null>(null)
  const [scannerHint, setScannerHint] = useState<string>('')
  const [invalidCode, setInvalidCode] = useState<string | null>(null)
  const [extractedCode, setExtractedCode] = useState<string | null>(null) // 從條碼中擷取的可能櫃號
  const [extractedVesselRegNo, setExtractedVesselRegNo] = useState<string | null>(null) // 從 raw 前 6 碼擷取的船掛
  const [rawScannedCode, setRawScannedCode] = useState<string | null>(null) // 原始掃描結果
  const videoRef = useRef<HTMLVideoElement>(null)
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const streamRef = useRef<MediaStream | null>(null)
  const animFrameRef = useRef<number>(0)
  const hasDetectedRef = useRef(false)
  const lastDetectedRef = useRef('')
  const lastLogTimeRef = useRef(0)
  const onSuccessRef = useRef(onSuccess)
  onSuccessRef.current = onSuccess

  const stopScanner = () => {
    if (animFrameRef.current) {
      cancelAnimationFrame(animFrameRef.current)
      animFrameRef.current = 0
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop())
      streamRef.current = null
    }
    hasDetectedRef.current = false
    lastDetectedRef.current = ''
  }

  const startScanner = async () => {
    setScannerError(null)
    setScannerHint('')
    setInvalidCode(null)
    setExtractedCode(null)
    setExtractedVesselRegNo(null)
    setRawScannedCode(null)
    hasDetectedRef.current = false
    lastDetectedRef.current = ''

    try {
      console.log('[BarcodeDebug] zbar-wasm 掃描器啟動中')

      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: 'environment',
          width: { ideal: 1280 },
          height: { ideal: 720 },
        },
      })
      streamRef.current = stream

      const video = videoRef.current
      if (!video) return

      video.srcObject = stream
      video.setAttribute('playsinline', 'true')
      await video.play()

      console.log(`[BarcodeDebug] 相機啟動完成，解析度: ${video.videoWidth}x${video.videoHeight}`)

      // 動態載入 zbar-wasm WASM 模組
      console.log('[BarcodeDebug] 開始載入 zbar-wasm...')
      await loadZbar()
      if (!scanImageDataFn) {
        setScannerError('條碼掃描引擎載入失敗\n\n請使用手動輸入功能')
        return
      }

      const canvas = canvasRef.current
      if (!canvas) return
      const ctx = canvas.getContext('2d', { willReadFrequently: true })
      if (!ctx) return

      // 掃描迴圈
      let frameCount = 0
      const scan = async () => {
        if (hasDetectedRef.current || !streamRef.current) return

        frameCount++
        // 每 5 幀掃描一次（約 6fps），降低 CPU 負擔
        if (frameCount % 5 === 0) {
          canvas.width = video.videoWidth
          canvas.height = video.videoHeight
          ctx.drawImage(video, 0, 0)

          try {
            const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height)
            const symbols = await scanImageDataFn!(imageData)

            if (symbols.length > 0) {
              const result = symbols[0]
              const rawText = result.decode()
              // 移除控制字元和空白
              const trimmedText = rawText.replace(/[^\x21-\x7E]/g, '')
              const typeName = result.typeName || 'unknown'

              console.log(`[BarcodeDebug] 偵測到: ${trimmedText} (格式: ${typeName})`)

              // 連續 2 次相同結果才確認
              if (lastDetectedRef.current === trimmedText) {
                hasDetectedRef.current = true
                console.log(`[BarcodeDebug] 掃描確認: ${trimmedText}`)

                stopScanner()

                // ISO 6346 格式驗證
                if (/^[A-Z]{4}\d{7}$/.test(trimmedText)) {
                  setScannerHint('✓ 辨識成功！')
                  onSuccessRef.current(trimmedText, trimmedText)
                } else {
                  // 嘗試從字串中擷取可能的貨櫃號碼（4字母+7數字）
                  const match = trimmedText.match(/[A-Z]{4}\d{7}/)
                  if (match) {
                    console.log(`[BarcodeDebug] 從 ${trimmedText} 擷取到可能的櫃號: ${match[0]}`)
                    setScannerHint('')
                    setRawScannedCode(trimmedText)
                    setExtractedCode(match[0])
                    // 補登表單情境（showVesselRegNo=true）：raw 若符合「6 碼數字 + ISO 6346」也擷取船掛
                    if (showVesselRegNo) {
                      const vslMatch = trimmedText.match(/^(\d{6})[A-Z]{4}\d{7}$/)
                      setExtractedVesselRegNo(vslMatch ? vslMatch[1] : null)
                    } else {
                      setExtractedVesselRegNo(null)
                    }
                  } else {
                    console.log(`[BarcodeDebug] 不符合 ISO 6346 格式: ${trimmedText}`)
                    setScannerHint('')
                    setInvalidCode(trimmedText)
                  }
                }
                return
              } else {
                lastDetectedRef.current = trimmedText
              }
            }
          } catch (e) {
            console.error('[BarcodeDebug] zbar 解碼錯誤:', e)
          }

          // debug log 每 3 秒一次
          const now = Date.now()
          if (now - lastLogTimeRef.current > 3000) {
            lastLogTimeRef.current = now
            console.log('[BarcodeDebug] 持續掃描中，尚未辨識到條碼')
          }
        }

        animFrameRef.current = requestAnimationFrame(scan)
      }

      console.log('[BarcodeDebug] zbar-wasm 掃描器初始化完成')
      animFrameRef.current = requestAnimationFrame(scan)
    } catch (error) {
      console.error('掃描器啟動失敗:', error)
      let errorMessage = '無法啟動相機'
      if (error instanceof Error) {
        if (error.message.includes('Permission') || error.message.includes('NotAllowed')) {
          errorMessage = '相機權限被拒絕，請在瀏覽器設定中允許相機權限'
        } else if (error.message.includes('NotFound')) {
          errorMessage = '找不到相機設備，請確認設備有相機功能'
        } else if (error.message.includes('NotReadable')) {
          errorMessage = '相機正被其他應用程式使用，請關閉其他使用相機的應用'
        } else {
          errorMessage = `啟動失敗: ${error.message}`
        }
      }
      setScannerError(`${errorMessage}\n\n請使用手動輸入功能`)
    }
  }

  useEffect(() => {
    if (!open) return

    // 延遲啟動，等 DOM 渲染完成
    const timer = setTimeout(() => {
      startScanner()
    }, 500)

    return () => {
      clearTimeout(timer)
      stopScanner()
      setScannerError(null)
      setScannerHint('')
      setInvalidCode(null)
    setExtractedCode(null)
    setExtractedVesselRegNo(null)
    setRawScannedCode(null)
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  const handleClose = () => {
    stopScanner()
    onClose()
  }

  return (
    <>
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: { borderRadius: 3 },
      }}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <QrCodeScannerIcon sx={{ color: '#2196f3', fontSize: 28 }} />
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            {title}
          </Typography>
        </Box>
        <IconButton onClick={handleClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        {scannerError ? (
          <Box sx={{ py: 4, textAlign: 'center' }}>
            <ErrorOutlineIcon sx={{ fontSize: 60, color: '#f44336', mb: 2 }} />
            <Typography variant="body1" color="error" sx={{ mb: 2 }}>
              {scannerError}
            </Typography>
            <Button
              variant="contained"
              onClick={handleClose}
              sx={{
                backgroundColor: '#6B705C',
                '&:hover': { backgroundColor: '#5A5F4A' },
              }}
            >
              關閉
            </Button>
          </Box>
        ) : (
          <Box>
            {scannerHint && (
              <Box
                sx={{
                  mb: 2,
                  p: 1.5,
                  backgroundColor: '#e8f5e9',
                  borderRadius: 2,
                  border: '2px solid #4caf50',
                  textAlign: 'center',
                }}
              >
                <Typography variant="body2" sx={{ fontWeight: 700, color: '#2e7d32' }}>
                  {scannerHint}
                </Typography>
              </Box>
            )}

            {/* 相機畫面 */}
            <Box
              sx={{
                position: 'relative',
                width: '100%',
                borderRadius: 2,
                overflow: 'hidden',
                backgroundColor: '#000',
              }}
            >
              <video
                ref={videoRef}
                style={{
                  width: '100%',
                  display: 'block',
                  borderRadius: 8,
                }}
                muted
                playsInline
              />
              {/* 隱藏的 canvas 用於擷取影格 */}
              <canvas ref={canvasRef} style={{ display: 'none' }} />
              {/* 掃描線 */}
              <Box
                sx={{
                  position: 'absolute',
                  top: '50%',
                  left: '5%',
                  right: '5%',
                  height: '2px',
                  backgroundColor: 'red',
                  boxShadow: '0 0 4px rgba(255, 0, 0, 0.6)',
                  zIndex: 10,
                  pointerEvents: 'none',
                  animation: 'scanPulse 1.5s ease-in-out infinite',
                  '@keyframes scanPulse': {
                    '0%, 100%': { opacity: 1 },
                    '50%': { opacity: 0.4 },
                  },
                }}
              />
            </Box>

            <Box
              sx={{
                mt: 2,
                p: 2,
                backgroundColor: '#e3f2fd',
                borderRadius: 2,
                border: '1px solid #2196f3',
              }}
            >
              <Typography variant="caption" sx={{ color: '#1976d2', fontWeight: 600, display: 'block', mb: 0.5 }}>
                請將條碼對準紅色掃描線
              </Typography>
              <Typography variant="caption" sx={{ color: '#ff9800', display: 'block', fontWeight: 600 }}>
                距離 10-20cm 效果最佳
              </Typography>
            </Box>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 2.5 }}>
        <Button
          variant="outlined"
          onClick={handleClose}
          sx={{
            borderColor: '#6B705C',
            color: '#6B705C',
            '&:hover': {
              borderColor: '#5A5F4A',
              backgroundColor: 'rgba(107, 112, 92, 0.04)',
            },
          }}
        >
          取消
        </Button>
      </DialogActions>
    </Dialog>

      {/* 格式不符確認彈窗 */}
      <Dialog open={!!invalidCode} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ pb: 1 }}>
          <Typography variant="h6" sx={{ fontWeight: 700, color: '#f57c00' }}>
            辨識結果確認
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            條碼可能不清晰，導致辨識結果可能有誤。請確認以下條碼是否正確：
          </Typography>
          <Box sx={{
            p: 2,
            backgroundColor: '#fff3e0',
            borderRadius: 2,
            border: '1px solid #ff9800',
            textAlign: 'center',
          }}>
            <Typography variant="h5" sx={{ fontWeight: 700, letterSpacing: 2 }}>
              {invalidCode}
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button
            variant="outlined"
            onClick={() => {
              setInvalidCode(null)
    setExtractedCode(null)
    setExtractedVesselRegNo(null)
    setRawScannedCode(null)
              startScanner()
            }}
            sx={{ borderColor: '#6B705C', color: '#6B705C' }}
          >
            重新掃描
          </Button>
          <Button
            variant="contained"
            onClick={() => {
              const code = invalidCode!
              setInvalidCode(null)
    setExtractedCode(null)
    setExtractedVesselRegNo(null)
    setRawScannedCode(null)
              onSuccess(code, code)
            }}
            sx={{
              backgroundColor: '#6B705C',
              '&:hover': { backgroundColor: '#5A5F4A' },
            }}
          >
            確認使用
          </Button>
        </DialogActions>
      </Dialog>

      {/* 從條碼擷取到可能的櫃號 */}
      <Dialog open={!!extractedCode} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ pb: 1 }}>
          <Typography variant="h6" sx={{ fontWeight: 700, color: '#2196f3' }}>
            偵測到貨櫃號碼
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 1 }}>
            掃描到的條碼內容：
          </Typography>
          <Box sx={{
            p: 1.5,
            mb: 2,
            backgroundColor: '#f5f5f5',
            borderRadius: 1,
            border: '1px solid #e0e0e0',
            textAlign: 'center',
          }}>
            <Typography variant="body2" sx={{ fontFamily: 'monospace', color: '#666' }}>
              {rawScannedCode}
            </Typography>
          </Box>
          <Typography variant="body2" sx={{ mb: 1 }}>
            自動偵測到以下{extractedVesselRegNo ? '船掛與' : ''}貨櫃號碼，確認是否使用？
          </Typography>
          {extractedVesselRegNo && (
            <Box sx={{
              p: 1.5,
              mb: 1.5,
              backgroundColor: '#fff4e5',
              borderRadius: 2,
              border: '1px solid #ff9800',
            }}>
              <Typography variant="caption" sx={{ color: '#e65100', fontWeight: 600, display: 'block', mb: 0.5 }}>
                船掛
              </Typography>
              <Typography variant="h5" sx={{ fontWeight: 700, letterSpacing: 2, textAlign: 'center' }}>
                {extractedVesselRegNo}
              </Typography>
            </Box>
          )}
          <Box sx={{
            p: 1.5,
            backgroundColor: '#e3f2fd',
            borderRadius: 2,
            border: '1px solid #2196f3',
          }}>
            {extractedVesselRegNo && (
              <Typography variant="caption" sx={{ color: '#1565c0', fontWeight: 600, display: 'block', mb: 0.5 }}>
                貨櫃號碼
              </Typography>
            )}
            <Typography variant="h5" sx={{ fontWeight: 700, letterSpacing: 2, textAlign: 'center' }}>
              {extractedCode}
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button
            variant="outlined"
            onClick={() => {
              setExtractedCode(null)
    setExtractedVesselRegNo(null)
    setRawScannedCode(null)
              startScanner()
            }}
            sx={{ borderColor: '#6B705C', color: '#6B705C' }}
          >
            重新掃描
          </Button>
          <Button
            variant="contained"
            onClick={() => {
              const code = extractedCode!
              const raw = rawScannedCode ?? code
              setExtractedCode(null)
    setExtractedVesselRegNo(null)
    setRawScannedCode(null)
              onSuccess(code, raw)
            }}
            sx={{
              backgroundColor: '#6B705C',
              '&:hover': { backgroundColor: '#5A5F4A' },
            }}
          >
            確認使用
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}

export default BarcodeScanner
